import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useBrand } from '../contexts/BrandContext';
import { formatCurrency } from '../lib/utils';
import {
    ArrowLeft, Headphones, Ticket, MapPin, Clock, Users, ShoppingBag, Shield,
    CreditCard, Loader2, User, CheckCircle2, ChevronDown, Search, Lock, Tag, X
} from 'lucide-react';
import { toast } from 'sonner';
import COUNTRIES from '../data/countries';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const API = process.env.REACT_APP_BACKEND_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);

const stripeAppearance = {
    theme: 'night',
    variables: {
        colorPrimary: '#c9a84c',
        colorBackground: '#0f1219',
        colorText: '#e4e4e7',
        colorDanger: '#ef4444',
        fontFamily: '"Outfit", system-ui, -apple-system, sans-serif',
        fontSizeBase: '15px',
        borderRadius: '8px',
        spacingUnit: '4px',
        colorTextPlaceholder: '#52525b',
    },
    rules: {
        '.Input': {
            border: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: 'rgba(255,255,255,0.04)',
            padding: '10px 14px',
        },
        '.Input:focus': {
            border: '1px solid rgba(201,168,76,0.5)',
            boxShadow: '0 0 0 1px rgba(201,168,76,0.15)',
        },
        '.Label': {
            color: 'rgba(255,255,255,0.55)',
            fontSize: '12px',
            fontWeight: '500',
        },
    },
};

function PaymentForm({ total, currency, orderId, onSuccess, clientSecret }) {
    const stripe = useStripe();
    const elements = useElements();
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [paymentReady, setPaymentReady] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setProcessing(true);
        setError(null);

        try {
            const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${API}/api/b2c/payment-return`,
                },
                redirect: 'if_required',
            });

            if (confirmError) {
                setError(confirmError.message);
                setProcessing(false);
            } else if (paymentIntent?.status === 'succeeded') {
                onSuccess();
            } else {
                setTimeout(() => onSuccess(), 3000);
            }
        } catch (err) {
            setError('Payment failed. Please try again.');
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5">
            <div data-testid="stripe-payment-element">
                <PaymentElement
                    onReady={() => setPaymentReady(true)}
                    options={{
                        layout: 'tabs',
                    }}
                />
            </div>

            {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="payment-error">{error}</div>}

            <Button type="submit" disabled={processing || !stripe || !paymentReady}
                className="w-full bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2 h-12 text-base font-semibold disabled:opacity-40" data-testid="pay-btn">
                {processing ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing Payment...</> : <><Lock className="w-4 h-4" /> Pay {formatCurrency(total, currency)}</>}
            </Button>
            <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1.5"><Shield className="w-3 h-3" /> Secured by Stripe &middot; 256-bit encryption</p>
        </form>
    );
}

function CountrySelector({ selected, onSelect }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = COUNTRIES.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) || c.dial.includes(search)
    );

    return (
        <div ref={ref} className="relative">
            <button type="button" onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 h-10 px-2.5 rounded-l-md border border-white/10 bg-white/5 text-white text-sm hover:bg-white/10 transition-colors min-w-[90px]"
                data-testid="country-selector-btn">
                <span className="text-base">{selected.flag}</span>
                <span className="text-white/80 text-xs">{selected.dial}</span>
                <ChevronDown className="w-3 h-3 text-white/40" />
            </button>
            {open && (
                <div className="absolute top-full left-0 mt-1 w-72 max-h-64 bg-[#1a1f2e] border border-white/10 rounded-lg shadow-2xl z-50 overflow-hidden" data-testid="country-dropdown">
                    <div className="p-2 border-b border-white/10">
                        <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
                            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search country..."
                                className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white placeholder:text-white/30 outline-none focus:border-gold/40"
                                data-testid="country-search-input" />
                        </div>
                    </div>
                    <div className="overflow-y-auto max-h-48">
                        {filtered.map(c => (
                            <button key={c.code} type="button" onClick={() => { onSelect(c); setOpen(false); setSearch(''); }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-white/5 transition-colors ${selected.code === c.code ? 'bg-gold/10' : ''}`}
                                data-testid={`country-option-${c.code}`}>
                                <span className="text-base">{c.flag}</span>
                                <span className="text-sm text-white flex-1">{c.name}</span>
                                <span className="text-xs text-white/50">{c.dial}</span>
                            </button>
                        ))}
                        {filtered.length === 0 && <p className="text-xs text-white/40 text-center py-3">No countries found</p>}
                    </div>
                </div>
            )}
        </div>
    );
}

function OtpInputBoxes({ length = 6, value, onChange }) {
    const inputs = useRef([]);
    const handleChange = (idx, val) => {
        if (!/^\d?$/.test(val)) return;
        const arr = value.split(''); arr[idx] = val;
        onChange(arr.join('').slice(0, length));
        if (val && idx < length - 1) inputs.current[idx + 1]?.focus();
    };
    const handleKeyDown = (idx, e) => { if (e.key === 'Backspace' && !value[idx] && idx > 0) inputs.current[idx - 1]?.focus(); };
    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length); onChange(pasted);
        inputs.current[Math.min(pasted.length, length - 1)]?.focus();
    };
    return (
        <div className="flex gap-2 justify-center" data-testid="otp-input-boxes">
            {Array.from({ length }).map((_, i) => (
                <input key={i} ref={el => inputs.current[i] = el} type="text" inputMode="numeric" maxLength={1}
                    value={value[i] || ''} onChange={e => handleChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} onPaste={handlePaste}
                    className="w-10 h-12 text-center text-lg font-semibold bg-white/5 border border-white/10 rounded-lg text-white focus:border-gold focus:ring-1 focus:ring-gold/30 outline-none transition-all"
                    data-testid={`otp-digit-${i}`} />
            ))}
        </div>
    );
}

export default function CheckoutPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const brand = useBrand();

    const listingId = params.get('listing');
    const optionId = params.get('option') || '';
    const adults = parseInt(params.get('adults') || '0');
    const children = parseInt(params.get('children') || '0');
    const infants = parseInt(params.get('infants') || '0');
    const visitDate = params.get('date') || '';
    const visitTime = params.get('time') || '';

    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(COUNTRIES.find(c => c.code === 'IT') || COUNTRIES[0]);
    const [travelerNames, setTravelerNames] = useState([]);

    const [otpSent, setOtpSent] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [verifyingOtp, setVerifyingOtp] = useState(false);
    const [otpCountdown, setOtpCountdown] = useState(0);

    const [showPayment, setShowPayment] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [orderId, setOrderId] = useState(null);
    const [initiatingPayment, setInitiatingPayment] = useState(false);

    const [discountCode, setDiscountCode] = useState('');
    const [appliedDiscount, setAppliedDiscount] = useState(null);
    const [applyingDiscount, setApplyingDiscount] = useState(false);

    const fetchListing = useCallback(async () => {
        try { const res = await fetch(`${API}/api/public/listings/${listingId}`); if (!res.ok) throw new Error(); setListing(await res.json()); }
        catch { navigate('/'); } finally { setLoading(false); }
    }, [listingId, navigate]);

    useEffect(() => { if (!listingId) { navigate('/'); return; } fetchListing(); }, [listingId, fetchListing, navigate]);

    useEffect(() => {
        if (listing) {
            const names = [];
            for (let i = 0; i < adults; i++) names.push({ name: '', dob: '', type: 'adult', label: `Adult ${i + 1}` });
            for (let i = 0; i < children; i++) names.push({ name: '', dob: '', type: 'child', label: `Child ${i + 1}` });
            for (let i = 0; i < infants; i++) names.push({ name: '', dob: '', type: 'infant', label: `Infant ${i + 1}` });
            setTravelerNames(names);
        }
    }, [listing, adults, children, infants]);

    useEffect(() => {
        if (otpCountdown <= 0) return;
        const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
        return () => clearTimeout(timer);
    }, [otpCountdown]);

    const handlePaymentSuccess = useCallback(async () => {
        toast.success('Payment successful!');
        for (let i = 0; i < 5; i++) {
            await new Promise(r => setTimeout(r, 1500));
            try {
                const res = await fetch(`${API}/api/b2c/order/${orderId}/status?session_id=`);
                const order = await res.json();
                if (order.payment_status === 'paid') break;
            } catch { }
        }
        navigate(`/order-success?order_id=${orderId}`);
    }, [orderId, navigate]);

    if (loading || !listing) return <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

    const fullPhoneNumber = selectedCountry.dial + phoneNumber;

    const handleSendOtp = async () => {
        if (!phoneNumber.trim()) { toast.error('Please enter your phone number'); return; }
        setSendingOtp(true);
        try {
            const res = await fetch(`${API}/api/checkout/send-otp`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone_number: fullPhoneNumber }) });
            if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
            setOtpSent(true); setOtpCode(''); setOtpCountdown(60); toast.success('Verification code sent!');
        } catch (err) { toast.error(err.message); } finally { setSendingOtp(false); }
    };

    const handleVerifyOtp = async () => {
        if (otpCode.length !== 6) { toast.error('Enter 6-digit code'); return; }
        setVerifyingOtp(true);
        try {
            const res = await fetch(`${API}/api/checkout/verify-otp`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone_number: fullPhoneNumber, code: otpCode }) });
            if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
            const data = await res.json();
            if (data.valid) { setPhoneVerified(true); toast.success('Phone verified!'); }
        } catch (err) { toast.error(err.message); } finally { setVerifyingOtp(false); }
    };

    const option = (listing.options || []).find(o => o.id === optionId);
    const pricing = option?.pricing || { adult: 0, child: 0, infant: 0 };
    const subtotal = (pricing.adult || 0) * adults + (pricing.child || 0) * children + (pricing.infant || 0) * infants;
    const discountAmount = appliedDiscount ? appliedDiscount.discount_amount : 0;
    const total = Math.max(subtotal - discountAmount, 0);
    const currency = listing.currency?.toUpperCase() || 'EUR';
    const canProceed = email.trim() && name.trim() && phoneVerified && travelerNames.every(t => t.name.trim());

    const handleApplyDiscount = async () => {
        const code = discountCode.trim().toUpperCase();
        if (!code) { toast.error('Please enter a discount code'); return; }
        setApplyingDiscount(true);
        try {
            const res = await fetch(`${API}/api/discounts/validate`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, listing_id: listingId, order_total: subtotal })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Invalid code');
            setAppliedDiscount(data);
            toast.success(`Discount applied: ${data.display}`);
        } catch (err) { toast.error(err.message); setAppliedDiscount(null); }
        finally { setApplyingDiscount(false); }
    };

    const removeDiscount = () => { setAppliedDiscount(null); setDiscountCode(''); };

    const handleProceedToPayment = async () => {
        if (!canProceed) { toast.error('Please complete all required fields'); return; }
        setInitiatingPayment(true);
        try {
            const res = await fetch(`${API}/api/b2c/checkout`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    listing_id: listingId, option_id: optionId, adults, children, infants,
                    visit_date: visitDate, visit_time: visitTime, email: email.trim(), name: name.trim(),
                    phone: phoneNumber.trim(), country_code: selectedCountry.dial,
                    traveler_names: travelerNames, origin_url: window.location.origin,
                    discount_code: appliedDiscount ? appliedDiscount.code : ''
                })
            });
            if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
            const data = await res.json();
            setClientSecret(data.client_secret);
            setOrderId(data.order_id);
            setShowPayment(true);
        } catch (err) { toast.error(err.message); } finally { setInitiatingPayment(false); }
    };

    const getImageUrl = (img) => img && (img.startsWith('http') ? img : `${API}${img}`);

    return (
        <div className="min-h-screen bg-[#0A0D14]">
            <header className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => showPayment ? setShowPayment(false) : navigate(-1)} className="text-white/60 hover:text-white" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-7 sm:h-9 max-w-[120px] sm:max-w-[160px] object-contain" />
                        ) : (
                            <span className="font-outfit font-semibold text-white">{showPayment ? 'Payment' : 'Checkout'}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground text-xs"><Shield className="w-3.5 h-3.5" /> Secure Checkout</div>
                </div>
            </header>

            <div className="pt-20 pb-24 px-4 sm:px-6 max-w-5xl mx-auto">
                {!showPayment ? (
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                        <div className="lg:col-span-3 space-y-6">
                            <h1 className="font-outfit text-xl sm:text-2xl font-bold text-white" data-testid="checkout-title">Complete Your Booking</h1>

                            <Card className="bg-[#121620] border-white/10">
                                <CardContent className="p-5 sm:p-6 space-y-5">
                                    <h3 className="font-outfit font-semibold text-white flex items-center gap-2"><User className="w-4 h-4 text-gold" /> Your Information</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                                        <div>
                                            <Label className="text-white/70 text-xs mb-1.5 block h-4 leading-4">Full Name *</Label>
                                            <Input value={name} onChange={e => setName(e.target.value)} placeholder="John Doe" className="bg-white/5 border-white/10 text-white" data-testid="checkout-name" />
                                        </div>
                                        <div>
                                            <Label className="text-white/70 text-xs mb-1.5 block h-4 leading-4">Email *</Label>
                                            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@example.com" className="bg-white/5 border-white/10 text-white" data-testid="checkout-email" />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-white/70 text-xs mb-1.5 block h-4 leading-4">Mobile Number *</Label>
                                        <div className="flex items-center">
                                            <CountrySelector selected={selectedCountry} onSelect={c => { setSelectedCountry(c); setPhoneVerified(false); setOtpSent(false); }} />
                                            <Input type="tel" value={phoneNumber} onChange={e => { setPhoneNumber(e.target.value.replace(/\D/g, '')); setPhoneVerified(false); setOtpSent(false); }}
                                                placeholder="333 123 4567" className="bg-white/5 border-white/10 text-white rounded-l-none border-l-0 flex-1" disabled={phoneVerified} data-testid="checkout-phone" />
                                            {!phoneVerified && (
                                                <Button type="button" onClick={handleSendOtp} disabled={sendingOtp || !phoneNumber.trim() || otpCountdown > 0}
                                                    className="ml-2 bg-gold/20 text-gold hover:bg-gold/30 border border-gold/30 text-xs px-3 whitespace-nowrap h-10" data-testid="send-otp-btn">
                                                    {sendingOtp ? <Loader2 className="w-3 h-3 animate-spin" /> : otpCountdown > 0 ? `Resend (${otpCountdown}s)` : otpSent ? 'Resend Code' : 'Send Code'}
                                                </Button>
                                            )}
                                            {phoneVerified && <div className="ml-2 flex items-center gap-1 text-emerald-400 text-xs whitespace-nowrap"><CheckCircle2 className="w-4 h-4" /> Verified</div>}
                                        </div>
                                        {otpSent && !phoneVerified && (
                                            <div className="mt-4 p-4 bg-white/3 rounded-lg border border-white/5 space-y-3">
                                                <p className="text-sm text-white/70 text-center">Enter the 6-digit code sent to <span className="text-white font-medium">{selectedCountry.flag} {fullPhoneNumber}</span></p>
                                                <OtpInputBoxes length={6} value={otpCode} onChange={setOtpCode} />
                                                <div className="flex justify-center">
                                                    <Button type="button" onClick={handleVerifyOtp} disabled={verifyingOtp || otpCode.length !== 6}
                                                        className="bg-gold text-[#0A0D14] hover:bg-gold/90 h-9 px-6 text-sm font-semibold" data-testid="verify-otp-btn">
                                                        {verifyingOtp ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Verifying...</> : 'Verify'}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>

                            {travelerNames.length > 0 && (
                                <Card className="bg-[#121620] border-white/10">
                                    <CardContent className="p-5 sm:p-6 space-y-4">
                                        <h3 className="font-outfit font-semibold text-white flex items-center gap-2"><Users className="w-4 h-4 text-gold" /> Traveler Details</h3>
                                        <p className="text-xs text-white/50">Full name as it appears on your ID - required for venue ticket verification on arrival</p>
                                        <div className="space-y-4">
                                            {travelerNames.map((t, i) => (
                                                <div key={`${t.type}-${t.label}`} className="p-3 rounded-lg bg-white/2 border border-white/5">
                                                    <div className="flex items-center gap-2 mb-2.5">
                                                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize font-medium ${t.type === 'adult' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : t.type === 'child' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-purple-500/10 text-purple-400 border border-purple-500/20'}`}>{t.label}</span>
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                                                        <div>
                                                            <Label className="text-white/50 text-xs mb-1.5 block h-4 leading-4">Full Name (as on ID) *</Label>
                                                            <Input value={t.name} onChange={e => { const u = [...travelerNames]; u[i] = { ...u[i], name: e.target.value }; setTravelerNames(u); }}
                                                                placeholder="Full name for venue ticket verification on arrival" className="bg-white/5 border-white/10 text-white" data-testid={`traveler-name-${i}`} />
                                                        </div>
                                                        <div>
                                                            <Label className="text-white/50 text-xs mb-1.5 block h-4 leading-4">Date of Birth</Label>
                                                            <Input type="date" value={t.dob} onChange={e => { const u = [...travelerNames]; u[i] = { ...u[i], dob: e.target.value }; setTravelerNames(u); }}
                                                                className="bg-white/5 border-white/10 text-white" data-testid={`traveler-dob-${i}`} />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        <div className="lg:col-span-2">
                            <Card className="bg-[#121620] border-white/10 sticky top-20" data-testid="order-summary">
                                <CardContent className="p-5 sm:p-6 space-y-5">
                                    <h3 className="font-outfit font-semibold text-white">Order Summary</h3>
                                    <div className="flex gap-3">
                                        {getImageUrl(listing.featured_image) ? <img src={getImageUrl(listing.featured_image)} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" /> : <div className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0"><ShoppingBag className="w-6 h-6 text-muted-foreground" /></div>}
                                        <div><p className="text-white font-medium text-sm">{listing.title}</p><p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{listing.city_name}</p></div>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        {option && <div className="flex items-center gap-1.5 text-muted-foreground">{option.has_tickets ? <Ticket className="w-3.5 h-3.5 text-gold" /> : <Headphones className="w-3.5 h-3.5 text-blue-400" />} {option.name}</div>}
                                        {visitDate && <div className="flex items-center gap-1.5 text-muted-foreground"><Clock className="w-3.5 h-3.5" /> {visitDate}{visitTime ? ` at ${visitTime}` : ''}</div>}
                                    </div>
                                    <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
                                        {adults > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Adult x {adults}</span><span className="text-white">{formatCurrency((pricing.adult || 0) * adults, currency)}</span></div>}
                                        {children > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Child x {children}</span><span className="text-white">{formatCurrency((pricing.child || 0) * children, currency)}</span></div>}
                                        {infants > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Infant x {infants}</span><span className="text-white">{formatCurrency((pricing.infant || 0) * infants, currency)}</span></div>}
                                    </div>

                                    {/* Discount Code */}
                                    <div className="border-t border-white/10 pt-4" data-testid="discount-section">
                                        {!appliedDiscount ? (
                                            <div className="flex gap-2">
                                                <div className="relative flex-1">
                                                    <Tag className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                                                    <input
                                                        data-testid="discount-code-input"
                                                        value={discountCode}
                                                        onChange={e => setDiscountCode(e.target.value.toUpperCase())}
                                                        onKeyDown={e => e.key === 'Enter' && handleApplyDiscount()}
                                                        placeholder="Discount code"
                                                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-gold/30 uppercase"
                                                    />
                                                </div>
                                                <Button onClick={handleApplyDiscount} disabled={applyingDiscount || !discountCode.trim()} variant="outline"
                                                    className="border-white/10 text-white/70 hover:text-white hover:border-gold/30 px-4 text-sm" data-testid="apply-discount-btn">
                                                    {applyingDiscount ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Apply'}
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-500/8 border border-emerald-500/20">
                                                <div className="flex items-center gap-2">
                                                    <Tag className="w-3.5 h-3.5 text-emerald-400" />
                                                    <span className="text-emerald-400 font-mono font-semibold text-sm">{appliedDiscount.code}</span>
                                                    <span className="text-emerald-400/60 text-xs">({appliedDiscount.display})</span>
                                                </div>
                                                <button onClick={removeDiscount} className="text-white/40 hover:text-white p-1" data-testid="remove-discount-btn">
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="border-t border-white/10 pt-4">
                                        {appliedDiscount && (
                                            <div className="space-y-1.5 mb-3 text-sm">
                                                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="text-white">{formatCurrency(subtotal, currency)}</span></div>
                                                <div className="flex justify-between"><span className="text-emerald-400">Discount ({appliedDiscount.display})</span><span className="text-emerald-400">-{formatCurrency(discountAmount, currency)}</span></div>
                                            </div>
                                        )}
                                        <div className="flex justify-between mb-4"><span className="text-white font-medium">Total</span><span className="text-xl font-outfit font-bold text-gold" data-testid="checkout-total">{formatCurrency(total, currency)}</span></div>
                                        <Button onClick={handleProceedToPayment} disabled={initiatingPayment || !canProceed}
                                            className="w-full bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2 h-12 text-base font-semibold disabled:opacity-40" data-testid="proceed-to-payment-btn">
                                            {initiatingPayment ? <><Loader2 className="w-5 h-5 animate-spin" /> Preparing...</> : <><CreditCard className="w-5 h-5" /> Proceed to Payment</>}
                                        </Button>
                                        {!phoneVerified && phoneNumber && <p className="text-xs text-amber-400/80 text-center mt-2">Please verify your phone number to proceed</p>}
                                        <p className="text-xs text-muted-foreground text-center mt-3 flex items-center justify-center gap-1"><Shield className="w-3 h-3" /> Powered by Stripe</p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                ) : (
                    <div className="max-w-xl mx-auto space-y-6">
                        <Card className="bg-[#121620] border-white/10">
                            <CardContent className="p-4 sm:p-5">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        {getImageUrl(listing.featured_image) ? <img src={getImageUrl(listing.featured_image)} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-muted-foreground" /></div>}
                                        <div><p className="text-white font-medium text-sm">{listing.title}</p><p className="text-xs text-muted-foreground">{option?.name}</p></div>
                                    </div>
                                    <span className="text-lg font-outfit font-bold text-gold">{formatCurrency(total, currency)}</span>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-[#121620] border-white/10" data-testid="payment-card">
                            <CardContent className="p-5 sm:p-6">
                                <h3 className="font-outfit font-semibold text-white flex items-center gap-2 mb-5">
                                    <CreditCard className="w-4 h-4 text-gold" /> Payment Details
                                </h3>
                                {clientSecret && (
                                    <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
                                        <PaymentForm total={total} currency={currency} orderId={orderId} onSuccess={handlePaymentSuccess} clientSecret={clientSecret} />
                                    </Elements>
                                )}
                            </CardContent>
                        </Card>

                        <div className="flex justify-center">
                            <button onClick={() => setShowPayment(false)} className="text-sm text-white/50 hover:text-white transition-colors" data-testid="back-to-details-btn">
                                Back to booking details
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
